<script setup lang='ts'>


import {useI18n} from "vue-i18n";
import {useYachtStore} from "@/stores/yachtStore";
import {createInquiry} from "@/services/trip.service";
import {computed, ref, watch} from "vue";
import {Offer} from "@/models/Offer";
import CustomButton from "@/components/Button/Button.vue";
import {useReCaptcha} from "vue-recaptcha-v3";
import {useFormatStore} from "@/stores/formatStore";
import {useCurrencyInput} from 'vue-currency-input'
import {trackEvent} from "@/services/event.service";
import { utils } from '@/services/client'
import SpecialRequest from "@/components/SpecialRequest/SpecialRequest.vue";
import {RouterLink} from "vue-router";
import {VPhoneInput} from "v-phone-input";
import {parsePhoneNumber} from "awesome-phonenumber";

const formatDate = utils.formatDate;

const {executeRecaptcha, recaptchaLoaded} = useReCaptcha()

const {t} = useI18n(); // Import the translation function


const yachtStore = useYachtStore();
const formatStore = useFormatStore();

const bookingForm = ref({})
const messageType = ref('')

const valid = ref(false)
const name = ref('')
const email = ref('')
const phone = ref('')
const comment = ref('')
const price = ref(formatStore.round(yachtStore.priceDetails.offerPrice))
const accepts = ref(false)

const reference = ref('')
const booked = ref(false);
const loading = ref(false);
const dialog = ref(false);
const errorStatusCode = ref(null);


const leastOffer = computed(() => {
  return formatStore.round(price.value * 0.90);
})

const {inputRef, formattedValue, numberValue,setValue} = useCurrencyInput({
  currency: formatStore.currency.code,
  hideCurrencySymbolOnFocus: false,
  hideGroupingSeparatorOnFocus: false,
  precision: 2,
  valueRange: {min: leastOffer.value},
});

watch(()=> price,(value)=>{
  setValue(value.value);
})

const openDialog = () => {
  trackEvent('cosmosy.ga.yachtPage.clickRequestQuote', {
    event_label: 'Click Request Quote',
  });

  dialog.value = true
}
const closeDialog = () => {
  trackEvent('cosmosy.ga.yachtPage.closeRequestQuote', {
    event_label: 'Close Request Quote',
  });
  dialog.value = false
}


const requestBooking = () => {


  messageType.value = ''

  bookingForm.value.validate()
      .then(async (result) => {
        if (!result.valid) {
          return
        }

        trackEvent('cosmosy.ga.yachtPage.submitRequestQuote', {
          event_label: 'Submit Request Quote',

          userData: {
            fullname: name.value,
            email: email.value,
            phone: parsePhoneNumber(phone.value).number?.international,
            boat_type: yachtStore.yacht.category.name,
            country: yachtStore.yacht.location.country,
            region: yachtStore.yacht.location.name,
            value: formatStore.formatNumber(
                formatStore.formatCurrencyNumeric(yachtStore.priceDetails.total)
            ),
            currency: formatStore.currency.code,
          }
        });

        await recaptchaLoaded()
        await executeRecaptcha('login')

        loading.value = true;
        const priceDetails = yachtStore.priceDetails
        const selectedAmenities = [...yachtStore.mandatoryAmenities, ...yachtStore.selectedAmenities]
        const notSelectedAmenities = yachtStore.optionalAmenities.filter((a) => {
          return selectedAmenities.indexOf(a.id) < 0

        });
        const offer: Offer = {
          basePrice: priceDetails.basePrice,
          offerPrice: formatStore.round(formatStore.fromCurrency(numberValue.value,formatStore.currency.code)),
          yachtId: yachtStore.yacht.id,
          locationId: yachtStore.availabilities[1].location.id,
          persons: yachtStore.selections.persons,
          dateFrom: formatDate(yachtStore.selections.dateFrom),
          dateTo: formatDate(yachtStore.selections.dateTo),
          selectedAmenities: selectedAmenities,
          notSelectedAmenities: notSelectedAmenities,
          contactEmail: email.value,
          contactName: name.value,
          contactPhone: parsePhoneNumber(phone.value).number?.international,
          notes: comment.value
              + "\n "
              + "\n <p>Original Offer: "+ price.value+"<p/>"
              + "\n <p><p/>"
              + "\n <p>Currency: "+formatStore.currency.code+"</p>"
              + "\n <p>Rate: "+formatStore.currency.rate+"</p>"
              + "\n <p><p/>"
              + "\n <p>Offer In "+formatStore.currency.code+" : "+ numberValue.value+"</p>"
              + "\n <p>Offer In EUR: "+ formatStore.fromCurrency(numberValue.value,formatStore.currency.code)+"</p>"
              + "\n <p>Rounded Offer In EUR: "+(Math.floor(formatStore.fromCurrency(numberValue.value,formatStore.currency.code) /10) *10)+"</p>"

        }

        createInquiry(yachtStore.yacht.id, offer)
            .then((res) => {
              booked.value = true;
              messageType.value = "success";
              reference.value = res.data.uuid
            })
            .catch((error) => {
              messageType.value = "error";
              if (error.response && error.response.status) {
                errorStatusCode.value = error.response.status
              } else {
                errorStatusCode.value = null
              }
            })
            .finally(() => {
              loading.value = false;
            })

      })
}

</script>

<template>

  <CustomButton type="black"
                v-if="yachtStore.availabilityState !='loading' && yachtStore.hasAvailabilities"
                :rounded="true"
                :outlined="false"
                class="w-100 my-0"
                @click="openDialog"
  >
    <span v-show="!booked">{{ t('bookingRequest') }}</span>
    <!--    <span v-show="!booked">{{ t('yacht.control.price') }}</span>-->
    <span v-show="booked">{{ t('bookingRequested') }}</span>
  </CustomButton>

  <v-dialog max-width="700" v-model="dialog">
    <template v-slot:default="{ isActive }">
      <v-card v-show="!booked" class="my-5 c-card relative p-10">
        <div class="sticky top-0 right-0 flex justify-end z-50 pt-10 rounded-t-lg bg-bw-white">
          <button
              class="bg-transparent border-none p-2 hover:opacity-70 transition-opacity"
              @click="closeDialog"
          >
            <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18"/>
          </button>
        </div>

        <span class="!responsive-title-4 font-semibold py-2">
          {{ t('yacht.booking.dialog.title') }}
        </span>
        <v-card-text class="c-text">
          <p class=" my-2">{{ t('yacht.booking.dialog.instruction') }}</p>
          <!--          <p class=" my-2">{{ t('yacht.booking.dialog.offer') }}</p>-->
          <v-form ref="bookingForm" v-model="valid">
            <v-text-field
                :label="t('yacht.booking.dialog.name')"
                v-model="name"
                variant="underlined"
                required
                v-show="!booked"
                :disabled="loading"
                :rules="[
                    v => !!v || t('yacht.booking.dialog.required.name'),
                    v => /^[\p{L}\p{M} ]+$/u.test(v) || t('yacht.booking.dialog.invalid.name')
                ]"
            ></v-text-field>

            <v-phone-input
                v-model="phone"
                :default-country="'GR'"
                :invalidMessage = "t('enterValidPhone')"
                :label= "t('yacht.booking.dialog.phone')"
                v-show="!booked"
                :disabled="loading"
                :only-country-code="false"
                :return-object="true"
                required
                density="comfortable"
                :use-default-validation="false"
                :rules="[
                      v => !!v || t('assistance.fillRequired'),
                      v => /^[\d+ ]+$/.test(v) || t('assistance.validValue')
                  ]"
            />
            <v-text-field
                v-model="email"
                variant="underlined"
                placeholder="sailor@cosmosyachting.com"
                :label="t('yacht.booking.dialog.email')"
                required
                :disabled="loading"
                v-show="!booked"
                :rules="[
                    v => !!v || t('yacht.booking.dialog.required.email'),
                    v => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || t('yacht.booking.dialog.invalid.email')
                ]"
            ></v-text-field>


            <v-textarea
                v-model="comment"
                variant="underlined"
                :placeholder="t('yacht.booking.dialog.addQuestions')"
                :label="t('yacht.booking.dialog.details')"
                rows="3"
                :disabled="loading"
                v-show="!booked"
            ></v-textarea>

            <!--            <div class="boat-booking-price-offer">-->
            <!--              <v-text-field-->
            <!--                  v-model="formattedValue"-->
            <!--                  variant="underlined"-->
            <!--                  required-->
            <!--                  :placeholder="'> '+(formatStore.inCurrency(leastOffer))"-->
            <!--                  :rules="[v => !!v || t('yacht.booking.dialog.awayForFree'),-->
            <!--                            v => numberValue >= leastOffer || t('yacht.booking.dialog.chew')+' '+v,-->
            <!--                            v => numberValue <= price || t('yacht.booking.dialog.required.moreForBooking')+' '+v-->
            <!--                            ]"-->
            <!--                  :label="`${t('yacht.booking.dialog.namePrice')} (${formatStore.currency.symbol})` "-->
            <!--                  rows="3"-->
            <!--                  ref="inputRef"-->
            <!--                  :disabled="loading"-->
            <!--                  v-show="!booked"-->
            <!--              ></v-text-field>-->
            <!--            </div>-->
            <v-checkbox
                v-show="!booked"
                v-model="accepts"
                :disabled="loading"
                density="compact"
                required
                class="flex items-center space-x-1 text-xs sm:text-sm"
                :rules="[v => !!v || t('yacht.booking.dialog.required.pleaseAgree')]"
            >
              <template v-slot:label>
                {{ t('yacht.booking.dialog.required.iAgree') }}&nbsp;<a
                  class="link underline"
                  href="/privacy"
                  target="_blank"
                  @click.stop
              >
                {{ t('yacht.booking.dialog.required.privacyPolicy') }}
              </a>.
              </template>
            </v-checkbox>

          </v-form>

          <div v-if="loading" class="text-center mt-4">
            <v-progress-circular indeterminate></v-progress-circular>
            <p>{{ t('yacht.booking.dialog.message.loading') }}</p>
          </div>
          <div v-if="messageType == 'error'" class="text-center mt-4">
            <p>{{ t('yacht.booking.dialog.message.error') }}
              <span v-if="errorStatusCode">
                (Error code: {{ errorStatusCode }}).
              </span><br>
              {{ t('yacht.booking.dialog.message.tryAgain') }}

              <RouterLink class="mb-4 font-semibold text-bw-black hover:text-bw-darkGrey underline cursor-pointer" :to="`/${$i18n.locale}/inquiry`">
                {{ $t('sendInquiry') }}
              </RouterLink>.
            </p>
          </div>
        </v-card-text>

        <v-card-actions>
          <div class="flex flex-auto my-3 gap-3">
            <CustomButton type="black"
                          :rounded="true"
                          :outlined="false"
                          v-if="!booked"
                          :disabled="loading"
                          @click="requestBooking"
            >
              {{ t('yacht.booking.dialog.btn.reserve') }}
            </CustomButton>

          </div>
        </v-card-actions>
      </v-card>
      <v-card v-show="booked">
        <div v-if="messageType == 'success'" class="text-center  p-5">
          <div class="my-5" v-html="t('yacht.booking.dialog.message.success',{'reference':reference})"/>

          <v-card-actions>
            <CustomButton type="outline-black"
                          :rounded="true"
                          :disabled="loading"
                          class="flex-1"
                          @click="closeDialog"
            >
              {{ t('global.ui.btn.close') }}
            </CustomButton>
          </v-card-actions>
        </div>
      </v-card>
    </template>
  </v-dialog>

</template>
<style>
.boat-booking-price-offer .v-field__input {
  font-size: 2em;

}
.c-card {
  padding: 0px 40px  40px 40px!important;  /* Equivalent to p-10 in Tailwind */
  border-radius: 12px !important;
}
.c-text{
  padding: 0!important;
}
.v-card-actions{
  padding: 0;
}
.v-selection-control__wrapper{
  height: 15px!important;
  width: 15px!important;
  margin-right: 10px!important;
}
</style>
